import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { client } from "@/api"

// A per-clip upload row (member of a session). Returned by the children endpoint
// and rendered as an expanded row under a session.
export interface UploadBatch {
  public_id: string
  game_public_id: string | null
  source: string
  phase: string
  status: string
  total_items: number
  completed_items: number
  failed_items: number
  latest_revision: number
  total_bytes: number | null
  failure_reason: string | null
  clip_public_id: string | null
  created_at: string
  updated_at: string
  user: {
    public_id: string
    name: string | null
    email: string
  }
}

// A crop-session roll-up computed SERVER-side over the FULL dataset (#1028), so
// counts are accurate even when a session spans more than one page.
export interface UploadSession {
  session_id: string
  game_public_id: string | null
  source: string
  itemCount: number
  completed: number
  failed: number
  inProgress: number
  cancelled: number
  totalBytes: number | null
  status: "in_progress" | "failed" | "completed" | "cancelled"
  startedAt: string
  endedAt: string
  isRollup: boolean
  // Present only for singletons (isRollup === false): the lone batch's detail,
  // so it renders as a standalone row without fetching the children endpoint.
  batch?: UploadBatch
  user: {
    public_id: string
    name: string | null
    email: string
  }
}

interface UploadsResponse {
  sessions: UploadSession[]
}

interface UploadSessionMembersResponse {
  members: UploadBatch[]
}

interface UploadDetailResponse extends Omit<UploadBatch, "user"> {
  user: {
    public_id: string
    name: string | null
    email: string
  }
  sessions: Array<{
    public_id: string
    push_token: string
    bundle_id: string
    environment: string
    status: string
    last_sent_revision: number
    created_at: string
    updated_at: string
  }>
}

export interface UploadStats {
  batches: {
    total: number
    active: number
    completed: number
    failed: number
    cancelled: number
    lastHour: number
  }
  sessions: {
    total: number
    active: number
    ended: number
    superseded: number
    invalid: number
    withPushes: number
    avgRevisions: number
  }
  devices: {
    total: number
    active: number
  }
}

type UploadStatus = "all" | "in_progress" | "completed" | "failed" | "cancelled"
type UploadSource = "all" | "web" | "mobile" | "desktop"

// Backend caps `limit` at 100 (see admin/uploads listQuerySchema) and now
// paginates BY SESSION (#1028), so a page is up to 100 crop-session roll-ups.
export const UPLOADS_PAGE_SIZE = 100

export function useUploads({
  status = "all" as UploadStatus,
  source = "all" as UploadSource,
}: { status?: UploadStatus; source?: UploadSource } = {}) {
  return useInfiniteQuery<UploadsResponse>({
    queryKey: ["admin", "uploads", { status, source }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = pageParam as number
      const response = await client.admin.uploads.$get({
        query: {
          limit: UPLOADS_PAGE_SIZE.toString(),
          offset: offset.toString(),
          ...(status !== "all" ? { status: status as Exclude<UploadStatus, "all"> } : {}),
          ...(source !== "all" ? { source: source as Exclude<UploadSource, "all"> } : {}),
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch uploads: ${response.status}`)
      }

      return response.json() as Promise<UploadsResponse>
    },
    // A short page means we've reached the end; otherwise the next offset is the
    // count of SESSIONS loaded so far.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.sessions.length < UPLOADS_PAGE_SIZE
        ? undefined
        : allPages.reduce((n, p) => n + p.sessions.length, 0),
    // Poll while any session still has in-progress rows so counts advance live
    // (#818/#1028); fall idle otherwise. Key on inProgress > 0, NOT
    // status === "in_progress": rollup status ranks failed above in_progress, so
    // a session with an early failure plus still-uploading clips reads "failed" —
    // keying on status would stop polling while it's actively uploading.
    // useInfiniteQuery refetches all loaded pages, so polling keeps accumulated
    // history rather than collapsing to page one.
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.sessions.some((s) => s.inProgress > 0))
        ? 4000
        : false,
  })
}

// Member rows for one session, fetched lazily when a roll-up is expanded.
// Handles both real session ids and 'solo_<id>' synthetic singleton ids.
export function useUploadSession(sessionId: string | null) {
  return useQuery<UploadSessionMembersResponse>({
    queryKey: ["admin", "uploads", "session", sessionId],
    queryFn: async () => {
      const response = await client.admin.uploads.sessions[":sessionId"].$get({
        param: { sessionId: sessionId as string },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch upload session: ${response.status}`)
      }

      return response.json() as Promise<UploadSessionMembersResponse>
    },
    enabled: !!sessionId,
    // Poll while any member is still uploading so the expanded per-file progress
    // stays live — the parent session poll refetches sessions, not these members.
    refetchInterval: (query) =>
      query.state.data?.members.some((m) => m.status === "in_progress") ? 4000 : false,
  })
}

export function useUploadDetail(batchPublicId: string) {
  return useQuery<UploadDetailResponse>({
    queryKey: ["admin", "uploads", batchPublicId],
    queryFn: async () => {
      const response = await client.admin.uploads[":batchPublicId"].$get({
        param: { batchPublicId },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch upload detail: ${response.status}`)
      }

      return response.json() as Promise<UploadDetailResponse>
    },
    enabled: !!batchPublicId,
  })
}

export function useUploadStats() {
  return useQuery<UploadStats>({
    queryKey: ["admin", "uploads", "stats"],
    queryFn: async () => {
      const response = await client.admin.uploads.stats.$get()

      if (!response.ok) {
        throw new Error(`Failed to fetch upload stats: ${response.status}`)
      }

      return response.json() as Promise<UploadStats>
    },
    refetchInterval: 30000, // Auto-refresh every 30s for monitoring
  })
}
